import React, { useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import GetRates from "../../../components/utils/GetRates";
import { formatAmount } from "../../../utils/formatTime";
import useAuthAxios from "../../../utils/baseAxios";
import OtpPin from "../../../components/OtpPin";
import ClipLoader from "react-spinners/ClipLoader";
import { errorMessage } from "../../../utils/errorMessage";
import { API } from "../../../constants/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import useUserAsset from "../../../hooks/useUserAssets";
import { useUser } from "../../../context/user-context";
import { invalidateActivityQueries } from "../../../utils/invalidateActivityQueries";

const TransferPreview = ({
  setOpenTransfer,
  setOpenTransferPreview,
  amount,
  selectedCoinObj,
  bankNumber,
  bankName,
  userBankName,
  fee,
  bankCode,
  setOpenTransferSuccess,
}: any) => {
  // Calculate BTC equivalent if available
  const coinRate = GetRates(selectedCoinObj?.abbr?.toLowerCase());
  const coinAmountInUsdt = Number(amount) / coinRate;
  const [addPin, setAddPin] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const queryClient = useQueryClient();
  const { userDetails, refetch } = useUser();
  const walletBalances = userDetails?.user.deexWalletBalances;
  const { walletBalanceInUsdt } = useUserAsset();
  const coinBalancee = walletBalances[selectedCoinObj?.abbr?.toLowerCase()];
  const coinBalanceeInUsdt =
    walletBalanceInUsdt[selectedCoinObj?.abbr?.toLowerCase()];
  const coinAmount = (coinAmountInUsdt * coinBalancee) / coinBalanceeInUsdt;

  const axiosInstance = useAuthAxios();

  const sellCrypto = async (data: any) => {
    const response = await axiosInstance.post(API.transferToBank, data);
    return response.data;
  };

  const completeSellCrypto = useMutation({
    mutationFn: sellCrypto,
    onSuccess: (r) => {
      toast.success(r.message);
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      invalidateActivityQueries(queryClient);
      refetch();
      setTimeout(() => {
        setOpenTransferPreview(false);
        setOpenTransferSuccess(true);
      }, 2000);
    },
    onError: (e) => {
      // console.log(e);
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  return (
    <div className="fixed inset-0  flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              setOpenTransfer(true);
              setOpenTransferPreview(false);
            }}
            className="flex items-center gap-2 "
          >
            <BsArrowLeft className=" text-gray-800 dark:text-gray-200 text-[16px]" />
          </button>
          <h4 className="text-gray-900  dark:text-gray-100 font-sora font-semibold text-[20px]">
            Preview withdraw
          </h4>
        </div>
        {addPin ? (
          <div className="mt-12">
            <h4 className="text-gray-900 text-center dark:text-gray-100 font-sora mb-4 font-semibold text-[20px]">
              Enter transaction PIN
            </h4>
            <OtpPin otp={otp} input={4} setOtp={setOtp} />
            <button
              onClick={() => {
                const data = {
                  asset: selectedCoinObj.abbr,
                  amount: Number(amount),
                  pin: otp,
                  recipient: {
                    accountNumber: bankNumber,
                    accountName: userBankName,
                    bankName: bankName,
                    bankCode: bankCode,
                  },
                };
                // console.log(data);
                completeSellCrypto.mutate(data);
              }}
              disabled={otp.length !== 4}
              className={
                otp.length !== 4
                  ? "mt-44 bg-gray-400 flex justify-center items-center text-gray-200 font-medium w-full h-[52px] rounded-xl"
                  : "mt-44 bg-secondary_button flex justify-center items-center text-white font-medium w-full h-[52px] rounded-xl"
              }
            >
              {completeSellCrypto.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Proceed"
              )}
            </button>
          </div>
        ) : (
          <div className="w-full mt-6">
            {/* Amount */}
            <div className="border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[15px] font-bold  mb-1">
                Amount
              </h4>
              <h4 className="text-[28px] font-bold text-[#13181B] font-sora dark:text-gray-100">
                {formatAmount(amount)} NGN
              </h4>

              <h4 className="mt-1 text-[14px] font-bold text-[#BE6B0A]">
                {formatAmount(coinAmount)} {selectedCoinObj?.abbr}
              </h4>
            </div>
            {/* Wallet */}
            <div className="border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[15px] font-bold mb-1">
                Wallet
              </h4>
              <h4 className="text-[18px] text-[#13181B] dark:text-gray-100">
                {selectedCoinObj?.name || "-"}
              </h4>
            </div>
            {/* Bank Details */}
            <div className="border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[15px] font-bold mb-1">
                Bank Details
              </h4>
              <h4 className="text-[18px] text-[#13181B] dark:text-gray-100">
                {bankNumber} - {bankName}
              </h4>
              <h4 className="mt-1 text-[14px] font-semibold text-[#BE6B0A]">
                {userBankName}
              </h4>
            </div>
            {/* Fee */}
            <div className="border-b border-gray-200 dark:border-gray-600 py-5">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-secondary_button text-[15px] font-bold">
                  DeeX Fee
                </h4>
              </div>
              <h4 className="text-[18px] text-[#13181B] dark:text-gray-100">
                {fee} NGN
              </h4>
            </div>
            {/* Continue Button */}
            <button
              onClick={() => {
                setAddPin(true);
              }}
              className="mt-12 bg-secondary_button flex justify-center items-center text-white text-[17px] font-medium h-[52px] w-full rounded-xl"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferPreview;
